/**
 * TransactionList
 *
 * 거래 목록 컴포넌트
 * - 날짜별 그룹화
 * - DateGroup + TransactionItem 조합
 * - 스크롤 감지하여 스크롤 중 클릭 오동작 방지 (BUG-012)
 *
 * @performance 관련 데이터(paymentMethods, users, categories)를 Props로 받아서 사용
 * 부모(TransactionListPage)에서 모든 데이터를 병렬 로딩하여 성능 개선
 */

import { useMemo, useState, useRef, useCallback } from 'react'
import type { Transaction } from '../types/Transaction'
import type { TransactionDisplayMode } from '../types/TransactionDisplayMode'
import type { Category } from '@/domains/category/types/Category'
import type { PaymentMethod } from '@/domains/payment/types/PaymentMethod'
import type { User } from '@/domains/user/types/User'
import { DateGroup } from './DateGroup'
import { TransactionItem } from './TransactionItem'

interface TransactionListProps {
  transactions: Transaction[]
  paymentMethods: PaymentMethod[]
  users: User[]
  categories: Category[]
  onTransactionLongPress?: (id: string) => void
  inModal?: boolean
  displayMode?: TransactionDisplayMode
}

interface DateGroupData {
  date: string
  transactions: Transaction[]
  totalIncome: number
  totalExpense: number
}

export function TransactionList({
  transactions,
  paymentMethods,
  users,
  categories,
  onTransactionLongPress,
  inModal = false,
  displayMode = 'full'
}: TransactionListProps) {
  // 스크롤 상태 관리 (BUG-012: 스크롤 중 클릭 오동작 방지)
  const [isScrolling, setIsScrolling] = useState(false)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * 스크롤 핸들러
   *
   * @reason
   * - BUG-012: 스크롤 중 거래 항목 클릭 오동작 방지
   * - 스크롤 중에는 isScrolling을 true로 설정
   * - 150ms 동안 스크롤 이벤트가 없으면 스크롤 완료로 판단
   */
  const handleScroll = useCallback(() => {
    setIsScrolling(true)

    // 기존 타임아웃 제거 (연속 스크롤 시 시간 연장)
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current)
    }

    // 150ms 후 스크롤 완료로 판단
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false)
    }, 150)
  }, [])

  /**
   * 날짜별로 그룹화 (날짜 내림차순)
   *
   * @performance useMemo로 메모이제이션하여 불필요한 재계산 방지
   */
  const groupedTransactions = useMemo(() => {
    const groups = new Map<string, DateGroupData>()

    // 거래를 날짜별로 그룹화
    for (const tx of transactions) {
      const date = tx.date

      if (!groups.has(date)) {
        groups.set(date, {
          date,
          transactions: [],
          totalIncome: 0,
          totalExpense: 0,
        })
      }

      const group = groups.get(date)!
      group.transactions.push(tx)

      if (tx.type === 'income') {
        group.totalIncome += tx.amount
      } else {
        group.totalExpense += tx.amount
      }
    }

    // 날짜 내림차순 정렬
    return Array.from(groups.values()).sort((a, b) => b.date.localeCompare(a.date))
  }, [transactions])

  // 빈 상태 처리
  if (groupedTransactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-gray-400">
        <span className="text-4xl mb-4">📭</span>
        <p className="text-lg">거래 내역이 없습니다</p>
      </div>
    )
  }

  return (
    <div
      className="bg-white dark:bg-gray-800"
      onScroll={handleScroll}
    >
      {groupedTransactions.map((group) => (
        <div key={group.date}>
          <DateGroup
            date={group.date}
            totalIncome={group.totalIncome}
            totalExpense={group.totalExpense}
            inModal={inModal}
          />
          {group.transactions.map((transaction) => (
            <TransactionItem
              key={transaction.id}
              transaction={transaction}
              paymentMethods={paymentMethods}
              users={users}
              categories={categories}
              onLongPress={onTransactionLongPress}
              displayMode={displayMode}
              isScrolling={isScrolling}
            />
          ))}
        </div>
      ))}
    </div>
  )
}
